import { useCallback, useEffect, useState } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import type { JsonArray, JsonObject, JsonValue } from '../types/json';
import {
  getChatbotConfig,
  updateChatbotConfigSection,
  type ChatbotConfig as ChatbotConfigType
} from '../utils/chatbotConfigAccess';

// Define types for different config sections
interface ChatbotConfig {
  colors?: JsonObject;
  texts?: JsonObject;
  quick_actions?: JsonArray;
  mini_apps?: JsonArray;
  button_colors?: JsonObject;
  logos?: JsonObject;
  ai_settings?: JsonObject;
  calendly_config?: JsonObject;
  theme_settings?: JsonObject;
  widget_settings?: JsonObject;
  multilingual_settings?: JsonObject;
  settings?: JsonObject;
}

// Type for valid config section keys
type ConfigSectionKey = keyof Omit<ChatbotConfigType, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>;

interface UseChatbotConfigReturn {
  config: ChatbotConfig | null;
  isLoading: boolean;
  error: Error | null;
  updateConfig: (section: keyof ChatbotConfig, data: JsonValue) => Promise<void>;
  updateConfigField: (section: keyof ChatbotConfig, field: string, value: JsonValue) => Promise<void>;
  getConfigSection: <T>(section: keyof ChatbotConfig) => T | undefined;
}

// Type-safe helper functions
function getConfigSection<T>(config: ChatbotConfig | null, section: keyof ChatbotConfig): T | undefined {
  if (!config) return undefined;
  const sectionData = config[section];
  return sectionData as unknown as T;
}

/**
 * Hook for accessing and updating chatbot configurations
 * @param configSection Optional specific section to retrieve from config
 * @returns Config data, loading state, error state, and update functions
 */
export function useChatbotConfig(configSection?: keyof ChatbotConfig): UseChatbotConfigReturn {
  const { chatbotPublicId, isLoading: isChatbotLoading } = useChatbot();
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch the configuration
  useEffect(() => {
    const fetchConfig = async () => {
      if (!chatbotPublicId || isChatbotLoading) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await getChatbotConfig(chatbotPublicId);
        if (!data) {
          throw new Error('Failed to fetch config');
        }

        setConfig(data as unknown as ChatbotConfig);
      } catch (err) {
        console.error('Error fetching chatbot config:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch config'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [chatbotPublicId, isChatbotLoading, configSection]);

  // Update a section of the configuration
  const updateConfig = useCallback(
    async (section: keyof ChatbotConfig, data: JsonValue) => {
      if (!chatbotPublicId) {
        throw new Error('No chatbot ID available');
      }

      try {
        // Use the centralized utility instead of direct Supabase query
        const success = await updateChatbotConfigSection(
          section as ConfigSectionKey, 
          data, 
          chatbotPublicId
        );

        if (!success) {
          throw new Error(`Failed to update ${section}`);
        }

        // Update local state
        setConfig(prev => {
          if (!prev) return null;
          const newConfig = { ...prev };
          (newConfig as Record<keyof ChatbotConfig, JsonValue>)[section] = data;
          return newConfig;
        });
      } catch (err) {
        console.error(`Error updating ${section}:`, err);
        throw err;
      }
    },
    [chatbotPublicId]
  );

  // Update a specific field within a section
  const updateConfigField = useCallback(
    async (section: keyof ChatbotConfig, field: string, value: JsonValue) => {
      if (!chatbotPublicId || !config) {
        throw new Error('No chatbot ID or config available');
      }

      const sectionData = (config as Record<keyof ChatbotConfig, JsonValue>)[section] as JsonObject || {};
      const updatedSection = { ...sectionData, [field]: value };

      await updateConfig(section, updatedSection);
    },
    [chatbotPublicId, config, updateConfig]
  );

  // Get a typed config section
  const getConfigSectionCallback = useCallback(
    <T>(section: keyof ChatbotConfig): T | undefined => {
      return getConfigSection<T>(config, section);
    },
    [config]
  );

  return {
    config: configSection && config ? { [configSection]: (config as Record<keyof ChatbotConfig, JsonValue>)[configSection] } as ChatbotConfig : config,
    isLoading: isLoading || isChatbotLoading,
    error,
    updateConfig,
    updateConfigField,
    getConfigSection: getConfigSectionCallback
  };
}